import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import CoachingService from "../../services/CoachingService";
import LiveAvatarCoach from "./LiveAvatarCoach";
import DebriefSummarySection from "./DebriefSummarySection";
import Loader from "../loader";
import "./coaching.css";

const TARGET_SAMPLE_RATE = 16000;
/**
 * Safety net after session ending when LiveAvatar is dead / never ready again —
 * the sentence queue cannot drain and tryRevealSummary would wait forever.
 * Long enough for multi-sentence closing speech when the avatar is healthy
 * (LiveAvatarCoach also enforces a 60s per-sentence speak timeout).
 * On each tick: force-clear only if avatar is not ready; otherwise reschedule.
 */
const SESSION_ENDING_FALLBACK_MS = 180000;
const GENERATING_SUMMARY_PENDING =
  "Your session has ended successfully. We're now preparing your report. Please wait a moment.";

/** Static per-phase UI policy. Dynamic labels (thinking/ending) resolved at render. */
const PHASE_UI = {
  paused: {
    hint: "paused",
    micEnabled: false,
    composerEnabled: false,
    placeholder: "Continue with coach to send a response…",
    avatarBusy: true,
  },
  confirming: {
    hint: "none",
    micEnabled: false,
    composerEnabled: false,
    placeholder: "Confirm how to end the session…",
    avatarBusy: true,
  },
  ending: {
    hint: "none",
    micEnabled: false,
    composerEnabled: false,
    placeholder: "Session ending…",
    avatarBusy: true,
  },
  speaking: {
    hint: "none",
    micEnabled: false,
    composerEnabled: false,
    placeholder: "Coach is speaking…",
    avatarBusy: true,
  },
  thinking: {
    hint: "none",
    micEnabled: false,
    composerEnabled: false,
    placeholder: "Please wait…",
    avatarBusy: true,
  },
  listening: {
    hint: "recording",
    micEnabled: true,
    composerEnabled: false,
    placeholder: "Speak, then tap stop to send…",
    avatarBusy: true,
  },
  your_turn: {
    hint: "reply",
    micEnabled: true,
    composerEnabled: true,
    placeholder: "Type your response…",
    avatarBusy: false,
  },
  idle: {
    hint: "none",
    micEnabled: false,
    composerEnabled: false,
    placeholder: "Connecting…",
    avatarBusy: true,
  },
};

const floatTo16BitPCM = (float32Array) => {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < float32Array.length; i++) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
};

// Box-average decimation acts as a cheap anti-alias low-pass filter, which greatly
// reduces the garbled/wrong words caused by naive point-sampling when downsampling.
const resampleTo16kHz = (input, inputSampleRate) => {
  if (inputSampleRate === TARGET_SAMPLE_RATE) {
    return input;
  }
  const ratio = inputSampleRate / TARGET_SAMPLE_RATE;
  const outputLength = Math.floor(input.length / ratio);
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      sum += input[j];
      count += 1;
    }
    output[i] = count > 0 ? sum / count : input[start] || 0;
  }
  return output;
};

const createAudioContext = () => {
  // Use the device's native sample rate. Forcing a 16kHz context can make Chrome
  // capture silence from a 48kHz mic stream; we downsample to 16kHz ourselves instead.
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  return new AudioCtx();
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunkSize)
    );
  }
  return btoa(binary);
};

const CoachingDebrief = ({
  simPlayedSummaryId,
  simId,
  accountId,
  linkId,
  linkAuditId,
  simPlayUUID,
  personalInfo,
  onRequirePersonalInfo,
  registerStartAfterPersonalInfo,
  onStatusChange,
  onPhaseChange,
  onSummaryChange,
  onSummarySkipped,
  registerEndSession,
  onPageLoadingChange,
  onSessionEndingChange,
  children,
}) => {
  const [status, setStatus] = useState("idle");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [coachStreamText, setCoachStreamText] = useState("");
  const [currentSentence, setCurrentSentence] = useState(null);
  const [summary, setSummary] = useState(null);
  const [skipReason, setSkipReason] = useState(null);
  const [endWithoutSummaryOpen, setEndWithoutSummaryOpen] = useState(false);
  const [pendingSkipReason, setPendingSkipReason] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [wsReady, setWsReady] = useState(false);
  const [avatarSpeaking, setAvatarSpeaking] = useState(false);
  /** True from first coach_text/sentence until speech queue drains — blocks premature "Your turn". */
  const [coachTurnActive, setCoachTurnActive] = useState(false);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  /** Blocks UI while starting session or ending debrief (e.g. "Starting coaching…") */
  const [actionLoading, setActionLoading] = useState(null);
  const [avatarPaused, setAvatarPaused] = useState(false);
  const [resumeRequestId, setResumeRequestId] = useState(0);
  /** True after debrief_complete / summary_skipped until status becomes completed. */
  const [sessionEnding, setSessionEnding] = useState(false);

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const sentenceQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  /** LiveAvatar stream ready — do not start TTS until true (avoids burning sentence 1). */
  const avatarReadyRef = useRef(false);
  /** Mirrors currentSentence so disconnect can re-queue without stale closures. */
  const currentSentenceRef = useRef(null);
  /** True when avatar dropped mid-sentence; used to end turn if nothing left after reconnect. */
  const speakInterruptedRef = useRef(false);
  const coachBufferRef = useRef("");
  const chatEndRef = useRef(null);
  const startingRef = useRef(false);
  const personalInfoRef = useRef(personalInfo);
  personalInfoRef.current = personalInfo;
  const stopRecordingRef = useRef(null);
  /** True after user clicks Continue until LiveAvatar reports ready again. */
  const continuePendingRef = useRef(false);
  const sessionEndingRef = useRef(false);
  const summaryRef = useRef(null);
  const skipReasonRef = useRef(null);
  const revealFallbackTimerRef = useRef(null);
  const tryRevealSummaryRef = useRef(null);
  const markSessionEndingRef = useRef(null);

  const clearRevealFallback = useCallback(() => {
    if (revealFallbackTimerRef.current) {
      clearTimeout(revealFallbackTimerRef.current);
      revealFallbackTimerRef.current = null;
    }
  }, []);

  /**
   * Reveal the summary UI only when: session is ending, avatar speech is idle,
   * and we have a summary or skip reason. Uses refs to avoid stale WS closures.
   */
  const tryRevealSummary = useCallback(() => {
    if (!sessionEndingRef.current) {
      return;
    }
    const speechIdle =
      !isPlayingRef.current && sentenceQueueRef.current.length === 0;
    if (!speechIdle) {
      return;
    }

    const hasPayload = Boolean(summaryRef.current || skipReasonRef.current);
    if (!hasPayload) {
      setPending(GENERATING_SUMMARY_PENDING);
      setActionLoading(null);
      return;
    }

    // Stop LiveAvatar only after closing speech drains — not during summary LLM.
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_liveavatar" }));
    } else if (sessionId) {
      CoachingService().stopLiveAvatarSession(sessionId).catch(() => {});
    }

    clearRevealFallback();
    setPending(null);
    setActionLoading(null);
    setEndWithoutSummaryOpen(false);
    setStatus("completed");
  }, [clearRevealFallback, sessionId]);

  tryRevealSummaryRef.current = tryRevealSummary;

  const scheduleRevealFallback = useCallback(() => {
    if (revealFallbackTimerRef.current) {
      return;
    }
    revealFallbackTimerRef.current = setTimeout(() => {
      revealFallbackTimerRef.current = null;
      if (!sessionEndingRef.current) {
        return;
      }

      const speechIdle =
        !isPlayingRef.current && sentenceQueueRef.current.length === 0;
      if (speechIdle) {
        tryRevealSummaryRef.current?.();
        return;
      }

      // Avatar still healthy — let the queue finish; do not cut long closing speech.
      if (avatarReadyRef.current) {
        scheduleRevealFallback();
        return;
      }

      // Dead / stuck-ready: force speech idle so the summary UI can reveal.
      sentenceQueueRef.current = [];
      isPlayingRef.current = false;
      currentSentenceRef.current = null;
      speakInterruptedRef.current = false;
      setCurrentSentence(null);
      setAvatarSpeaking(false);
      setCoachTurnActive(false);
      tryRevealSummaryRef.current?.();
    }, SESSION_ENDING_FALLBACK_MS);
  }, []);

  const markSessionEnding = useCallback(() => {
    // Stop mic without sending end_audio (would start another coach turn).
    stopRecordingRef.current?.({ sendEndAudio: false });

    if (!sessionEndingRef.current) {
      sessionEndingRef.current = true;
      setSessionEnding(true);
      scheduleRevealFallback();
    }

    tryRevealSummaryRef.current?.();
  }, [scheduleRevealFallback]);

  markSessionEndingRef.current = markSessionEnding;

  const hasPersonalInfo = Boolean(
    personalInfo?.firstName && personalInfo?.lastName && personalInfo?.email
  );

  const playNextSentence = useCallback(() => {
    // Hold the queue until LiveAvatar can speak — prevents burning sentence 1 on a half-ready session.
    if (
      !avatarReadyRef.current ||
      isPlayingRef.current ||
      sentenceQueueRef.current.length === 0
    ) {
      return;
    }
    isPlayingRef.current = true;
    setAvatarSpeaking(true);
    const next = sentenceQueueRef.current.shift();
    currentSentenceRef.current = next;
    setCurrentSentence(next);
  }, []);

  const handleSentenceComplete = useCallback(() => {
    isPlayingRef.current = false;
    setAvatarSpeaking(false);
    currentSentenceRef.current = null;
    setCurrentSentence(null);
    playNextSentence();
    // Coach fully finished this turn — leave mic off so the user chooses when to reply.
    if (!isPlayingRef.current && sentenceQueueRef.current.length === 0) {
      setCoachTurnActive(false);
      if (sessionEndingRef.current) {
        tryRevealSummary();
      }
    }
  }, [playNextSentence, tryRevealSummary]);

  /**
   * Avatar stream dropped or restarted. Release the playing lock and re-queue the
   * interrupted sentence so reconnect can resume (or end the turn if nothing left).
   * When ready again, drain the queue (including sentences that arrived while connecting).
   */
  const handleAvatarReadyChange = useCallback(
    (isReady) => {
      if (!isReady) {
        avatarReadyRef.current = false;
        if (isPlayingRef.current) {
          isPlayingRef.current = false;
          setAvatarSpeaking(false);
          const interrupted = currentSentenceRef.current;
          currentSentenceRef.current = null;
          setCurrentSentence(null);
          if (interrupted) {
            sentenceQueueRef.current.unshift(interrupted);
          }
          speakInterruptedRef.current = true;
        }
        return;
      }

      avatarReadyRef.current = true;
      setAvatarPaused(false);
      playNextSentence();
      if (
        speakInterruptedRef.current &&
        !isPlayingRef.current &&
        sentenceQueueRef.current.length === 0
      ) {
        setCoachTurnActive(false);
        if (sessionEndingRef.current) {
          tryRevealSummary();
        }
      }
      speakInterruptedRef.current = false;
      // After Continue reconnect: leave mic off — user opens it or types.
      if (continuePendingRef.current) {
        continuePendingRef.current = false;
      }
    },
    [playNextSentence, tryRevealSummary]
  );

  const connectWebSocket = useCallback(
    (id) => {
      const url = CoachingService().getWebSocketUrl(id);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => setWsReady(true);

      const commitCoachMessage = () => {
        const text = coachBufferRef.current.trim();
        coachBufferRef.current = "";
        setCoachStreamText("");
        if (!text) return;
        setMessages((prev) => [...prev, { role: "COACH", content: text }]);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case "session_ready":
            setStatus("active");
            setAvatarPaused(false);
            setPending("Your coach is preparing…");
            break;
          case "coach_text":
            // Coach response started — hold "Your turn" until speech finishes.
            setCoachTurnActive(true);
            setPending(null);
            coachBufferRef.current += data.text || "";
            setCoachStreamText(coachBufferRef.current);
            break;
          case "coach_sentence":
            if (data.sentence?.text) {
              setCoachTurnActive(true);
              // Queue always; playNextSentence no-ops until avatarReadyRef is true.
              sentenceQueueRef.current.push(data.sentence.text);
              playNextSentence();
            }
            break;
          case "phase":
            commitCoachMessage();
            setCurrentPhase(data.phase ?? 0);
            break;
          case "transcript_final":
            if (data.text) {
              setMessages((prev) => [
                ...prev,
                { role: "PARTICIPANT", content: data.text },
              ]);
              setPending("Your coach is thinking…");
            } else {
              // No speech — stay on user's turn; do not start a coach turn.
              setCoachTurnActive(false);
              setPending(null);
              setError("No speech detected. Please try again and speak clearly.");
            }
            break;
          case "debrief_complete":
            commitCoachMessage();
            // Keep speaking the remaining coach sentences; reveal after speech idle.
            markSessionEndingRef.current?.();
            break;
          case "summary_generating":
            // Lock mic/text as soon as backend starts the summary LLM (before summary arrives).
            setActionLoading(null);
            setPending(GENERATING_SUMMARY_PENDING);
            markSessionEndingRef.current?.();
            break;
          case "summary":
            commitCoachMessage();
            setEndWithoutSummaryOpen(false);
            setPendingSkipReason(null);
            skipReasonRef.current = null;
            setSkipReason(null);
            summaryRef.current = data.summary;
            setSummary(data.summary);
            // Summary means completion is underway — lock input and reveal after speech.
            markSessionEndingRef.current?.();
            break;
          case "summary_decision":
            setPending(null);
            setActionLoading(null);
            if (data.generateSummary === false) {
              setPendingSkipReason(
                data.reason ||
                  "This conversation doesn’t have enough reflection for a debrief summary."
              );
              setEndWithoutSummaryOpen(true);
            }
            break;
          case "summary_skipped":
            commitCoachMessage();
            setEndWithoutSummaryOpen(false);
            summaryRef.current = null;
            setSummary(null);
            {
              const reason =
                data.reason ||
                "This conversation doesn’t have enough reflection for a debrief summary.";
              skipReasonRef.current = reason;
              setSkipReason(reason);
            }
            markSessionEndingRef.current?.();
            break;
          case "error":
            setCoachTurnActive(false);
            setPending(null);
            setActionLoading(null);
            setError(data.error);
            break;
          default:
            break;
        }
      };

      ws.onerror = (event) => {
        console.error("Coaching WebSocket error", {
          sessionId: id,
          readyState: ws.readyState,
          event,
          stack: new Error("Coaching WebSocket error").stack,
        });
        setActionLoading(null);
        setError("Connection to coaching service failed.");
      };
      ws.onclose = (event) => {
        // 1000 (normal) and 1005 (no status) are expected teardowns.
        if (event.code !== 1000 && event.code !== 1005) {
          console.error("Coaching WebSocket closed unexpectedly", {
            sessionId: id,
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });
        }
        wsRef.current = null;
        setWsReady(false);
      };
    },
    [playNextSentence]
  );

  const startSession = useCallback(
    async (infoOverride) => {
      // Ignore React click events — onClick passes the event as the first arg.
      const fromOverride =
        infoOverride &&
        typeof infoOverride === "object" &&
        !infoOverride.nativeEvent &&
        (infoOverride.firstName || infoOverride.lastName || infoOverride.email)
          ? infoOverride
          : null;
      const info = fromOverride || personalInfoRef.current;
      const ready = Boolean(
        String(info?.firstName || "").trim() &&
          String(info?.lastName || "").trim() &&
          String(info?.email || "").trim()
      );
      if (!ready) {
        setError(null);
        if (typeof onRequirePersonalInfo === "function") {
          onRequirePersonalInfo();
        } else {
          setError(
            "Please save your personal information before starting the coaching debrief."
          );
        }
        return;
      }
      if (
        startingRef.current ||
        actionLoading ||
        status === "active" ||
        status === "completed"
      ) {
        return;
      }
      startingRef.current = true;
      setError(null);
      setAvatarPaused(false);
      avatarReadyRef.current = false;
      setResumeRequestId(0);
      setActionLoading("Starting coaching…");
      const payload = {
        simPlayedSummaryId,
        simId,
        accountId,
        linkId,
        linkAuditId,
        simPlayUUID,
        participantFirstName: info.firstName,
        participantLastName: info.lastName,
        participantEmail: info.email,
        skip: false,
      };
      try {
        const response = await CoachingService().createOrResumeSession(payload);
        if (response?.status === 200 && response?.data?.sessionId) {
          setSessionId(response.data.sessionId);
          if (response.data.messages?.length) {
            setMessages(
              response.data.messages.map((m) => ({
                role: m.role,
                content: m.content,
              }))
            );
          }
          setCurrentPhase(response.data.currentPhase ?? 0);
          if (response.data.summary) {
            summaryRef.current = response.data.summary;
            setSummary(response.data.summary);
            setStatus("completed");
            setActionLoading(null);
          } else {
            sessionEndingRef.current = false;
            setSessionEnding(false);
            clearRevealFallback();
            setCoachTurnActive(false);
            setStatus("active");
            setActionLoading(null);
            connectWebSocket(response.data.sessionId);
          }
        } else {
          setError(response?.message || "Could not start coaching session.");
          setActionLoading(null);
        }
      } catch (e) {
        setError(e?.message || "Could not start coaching session.");
        setActionLoading(null);
      } finally {
        startingRef.current = false;
      }
    },
    [
      status,
      actionLoading,
      simPlayedSummaryId,
      simId,
      accountId,
      linkId,
      linkAuditId,
      simPlayUUID,
      onRequirePersonalInfo,
      connectWebSocket,
      clearRevealFallback,
    ]
  );

  useEffect(() => {
    if (typeof registerStartAfterPersonalInfo !== "function") {
      return undefined;
    }
    registerStartAfterPersonalInfo(startSession);
    return () => registerStartAfterPersonalInfo(null);
  }, [registerStartAfterPersonalInfo, startSession]);

  const handleAvatarPaused = useCallback(() => {
    continuePendingRef.current = false;
    // Drop in-progress capture without sending end_audio — user must Continue first.
    stopRecordingRef.current?.({ sendEndAudio: false });
    setAvatarPaused(true);
    setAvatarSpeaking(false);
    isPlayingRef.current = false;
    // Prefer re-queue via handleAvatarReadyChange(false); clear any leftover mirror.
    currentSentenceRef.current = null;
    setCurrentSentence(null);
    // Only end the coach turn if nothing left to speak after reconnect.
    if (sentenceQueueRef.current.length === 0) {
      setCoachTurnActive(false);
      speakInterruptedRef.current = false;
    }
    setError(null);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_liveavatar" }));
    }
  }, []);

  const continueCoach = useCallback(() => {
    setError(null);
    continuePendingRef.current = true;
    setAvatarPaused(false);
    setResumeRequestId((id) => id + 1);
  }, []);

  const startRecording = async () => {
    if (
      sessionEnding ||
      avatarPaused ||
      avatarSpeaking ||
      isRecording ||
      !!pending ||
      !!actionLoading ||
      !wsRef.current ||
      !wsReady
    ) {
      return;
    }
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          "Microphone is not available in this browser context. Open the simulation in its own tab if embedded."
        );
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      const audioContext = createAudioContext();
      audioContextRef.current = audioContext;
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      const inputSampleRate = audioContext.sampleRate;
      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        const resampled = resampleTo16kHz(input, inputSampleRate);
        const pcm = floatTo16BitPCM(resampled);
        wsRef.current.send(
          JSON.stringify({
            type: "audio_chunk",
            data: arrayBufferToBase64(pcm),
          })
        );
      };
      const silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      source.connect(processor);
      processor.connect(silentGain);
      silentGain.connect(audioContext.destination);
      setIsRecording(true);
    } catch (err) {
      const blockedInFrame =
        window.self !== window.top &&
        (err?.name === "NotAllowedError" || err?.name === "SecurityError");
      setError(
        blockedInFrame
          ? "Microphone is blocked inside the embedded player. Open the simulation in a new tab to use voice coaching."
          : "Microphone access is required for voice coaching. Check browser permissions and try again."
      );
    }
  };

  const stopRecording = ({ sendEndAudio = true } = {}) => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    if (
      sendEndAudio &&
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      wsRef.current.send(JSON.stringify({ type: "end_audio" }));
      setPending("Transcribing your response…");
    }
  };

  // Keep a live reference so session-ending can stop the mic without a stale closure.
  stopRecordingRef.current = stopRecording;

  const sendTextMessage = () => {
    const text = textInput.trim();
    if (
      !text ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      isRecording ||
      avatarPaused ||
      avatarSpeaking ||
      sessionEnding ||
      !!pending ||
      !!actionLoading
    ) {
      return;
    }
    // participant_text has no server echo, so add the You bubble optimistically.
    setMessages((prev) => [...prev, { role: "PARTICIPANT", content: text }]);
    wsRef.current.send(JSON.stringify({ type: "participant_text", text }));
    setTextInput("");
    setPending("Your coach is thinking…");
  };

  const endDebrief = useCallback(() => {
    if (
      actionLoading ||
      sessionEnding ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    if (isRecording) {
      stopRecording();
    }
    setError(null);
    setEndWithoutSummaryOpen(false);
    setActionLoading("Ending debrief…");
    setPending("Checking whether a summary can be created…");
    wsRef.current.send(JSON.stringify({ type: "end_debrief" }));
  }, [actionLoading, sessionEnding, isRecording]);

  const confirmEndWithoutSummary = useCallback(() => {
    if (
      actionLoading ||
      sessionEnding ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    setEndWithoutSummaryOpen(false);
    setActionLoading("Ending session…");
    setPending("Ending session…");
    wsRef.current.send(JSON.stringify({ type: "confirm_end_without_summary" }));
  }, [actionLoading, sessionEnding]);

  const cancelEndWithoutSummary = useCallback(() => {
    setEndWithoutSummaryOpen(false);
    setPendingSkipReason(null);
    setPending(null);
    setActionLoading(null);
  }, []);

  useEffect(() => {
    if (typeof onStatusChange === "function") {
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  useEffect(() => {
    if (typeof onSessionEndingChange === "function") {
      onSessionEndingChange(sessionEnding);
    }
  }, [sessionEnding, onSessionEndingChange]);

  useEffect(() => {
    if (typeof onPhaseChange === "function") {
      onPhaseChange(currentPhase);
    }
  }, [currentPhase, onPhaseChange]);

  useEffect(() => {
    if (typeof onSummaryChange === "function") {
      onSummaryChange(summary);
    }
  }, [summary, onSummaryChange]);

  useEffect(() => {
    if (typeof onSummarySkipped === "function") {
      onSummarySkipped(skipReason);
    }
  }, [skipReason, onSummarySkipped]);

  useEffect(() => {
    if (typeof registerEndSession !== "function") {
      return undefined;
    }
    registerEndSession(endDebrief);
    return () => registerEndSession(null);
  }, [registerEndSession, endDebrief]);

  // Drive full-page system Loader (EndTrack overlay) so header actions are blocked too.
  useEffect(() => {
    if (typeof onPageLoadingChange !== "function") {
      return undefined;
    }
    onPageLoadingChange(actionLoading);
    return () => onPageLoadingChange(null);
  }, [actionLoading, onPageLoadingChange]);

  useEffect(() => {
    return () => {
      clearRevealFallback();
      stopRecording({ sendEndAudio: false });
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const endEl = chatEndRef.current;
    if (!endEl) {
      return;
    }
    const sideBody = endEl.closest(".coaching-session-side-body");
    if (sideBody) {
      sideBody.scrollTop = sideBody.scrollHeight;
      return;
    }
    endEl.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, coachStreamText, pending]);

  const interactionPhase = (() => {
    if (avatarPaused) return "paused";
    if (endWithoutSummaryOpen) return "confirming";
    if (sessionEnding) return "ending";
    if (avatarSpeaking || coachTurnActive) return "speaking";
    if (pending || actionLoading) return "thinking";
    if (isRecording) return "listening";
    if (status === "active" && wsReady) return "your_turn";
    return "idle";
  })();

  const phaseUi = PHASE_UI[interactionPhase];

  const coachStatusLabel = (() => {
    switch (interactionPhase) {
      case "paused":
        return "Paused";
      case "confirming":
        return "Your turn";
      case "ending":
        return pending === GENERATING_SUMMARY_PENDING
          ? "Wrapping up…"
          : pending || "Thinking…";
      case "speaking":
        return "Speaking…";
      case "thinking":
        return pending || actionLoading || "Thinking…";
      case "listening":
        return "Listening…";
      case "your_turn":
        return "Your turn";
      case "idle":
      default:
        return "Connecting…";
    }
  })();

  const micDisabled = !phaseUi.micEnabled;
  const composerDisabled = !phaseUi.composerEnabled;
  const sendDisabled = !(phaseUi.composerEnabled && textInput.trim());
  const avatarBusy = phaseUi.avatarBusy;

  const micButton = !isRecording ? (
    <button
      type="button"
      className="coach-icon-btn coach-icon-btn-mic coach-icon-btn-mic-off"
      onClick={startRecording}
      disabled={micDisabled}
      title="Open microphone to speak"
      aria-label="Open microphone to speak"
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        aria-hidden="true"
        focusable="false"
      >
        {/* Mic-off: capsule + stand with diagonal slash */}
        <path
          fill="currentColor"
          d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V6c0-1.66-1.34-3-3-3s-3 1.34-3 3v.18l5.98 5.99zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"
        />
      </svg>
    </button>
  ) : (
    <button
      type="button"
      className="coach-icon-btn coach-icon-btn-mic coach-icon-btn-mic-active"
      onClick={stopRecording}
      disabled={micDisabled}
      title="Stop and send"
      aria-label="Stop and send"
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z"
        />
        <path
          fill="currentColor"
          d="M19 12a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V21a1 1 0 0 0 2 0v-2.07A7 7 0 0 0 19 12z"
        />
      </svg>
    </button>
  );

  const transcriptPanel = (
    <div className="coaching-chat coaching-chat-transcript">
      {messages.map((msg, idx) => {
        const isCoach = msg.role === "COACH";
        return (
          <div
            key={idx}
            className={`coaching-row ${
              isCoach ? "coaching-row-coach" : "coaching-row-participant"
            }`}
          >
            <span className="coaching-bubble-author">
              {isCoach ? "Coach" : "You"}
            </span>
            <div
              className={`coaching-bubble ${
                isCoach ? "coaching-bubble-coach" : "coaching-bubble-participant"
              }`}
            >
              {msg.content}
            </div>
          </div>
        );
      })}

      {coachStreamText && (
        <div className="coaching-row coaching-row-coach">
          <span className="coaching-bubble-author">Coach</span>
          <div className="coaching-bubble coaching-bubble-coach">
            {coachStreamText}
            <span className="coaching-caret" />
          </div>
        </div>
      )}

      {pending && !coachStreamText && (
        <div className="coaching-row coaching-row-coach">
          <span className="coaching-bubble-author">Coach</span>
          <div className="coaching-bubble coaching-bubble-coach coaching-bubble-pending">
            <span className="coaching-typing">
              <span />
              <span />
              <span />
            </span>
            <span className="coaching-pending-text">{pending}</span>
          </div>
        </div>
      )}

      <div ref={chatEndRef} />
    </div>
  );

  const sessionStage = (
    <div className="coaching-session-stage">
      {error && <div className="coaching-error coaching-error-on-stage">{error}</div>}
      <div className="coaching-session-avatar">
        <LiveAvatarCoach
          coachingSessionId={sessionId}
          active={status === "active"}
          isBusy={avatarBusy}
          allowRecovery={!sessionEnding}
          resumeRequestId={resumeRequestId}
          textToSpeak={currentSentence}
          statusLabel={coachStatusLabel}
          onSpeakingComplete={handleSentenceComplete}
          onPaused={handleAvatarPaused}
          onReadyChange={handleAvatarReadyChange}
          onError={(message) => {
            const text = String(message || "").toLowerCase();
            if (
              text.includes("needs to be connected") ||
              text.includes("send command event")
            ) {
              return;
            }
            setError(message);
          }}
        />
      </div>
      <div className="coaching-session-footer">
        {phaseUi.hint === "paused" && (
          <div className="coach-paused-bar" aria-live="polite">
            <p className="coach-paused-help">
              Coach disconnected to save connection. Continue to reconnect before
              speaking or sending.
            </p>
            <button
              type="button"
              className="btn btn-primary coach-continue-btn"
              onClick={continueCoach}
              disabled={!!actionLoading}
            >
              Continue with coach
            </button>
          </div>
        )}
        {phaseUi.hint === "recording" && (
          <p
            className="coaching-recording-hint coaching-recording-hint-on-stage"
            aria-live="polite"
          >
            <span className="coaching-rec-dot" aria-hidden="true" />
            Mic is on — Start speaking, then tap the Stop button when you're finished to send your message
          </p>
        )}
        {phaseUi.hint === "reply" && (
          <p
            className="coaching-reply-hint coaching-reply-hint-on-stage"
            aria-live="polite"
          >
            Open the mic to speak, or type a message below.
          </p>
        )}
        <div className="coach-input-row">
          <textarea
            className="coach-input-row-field"
            rows={1}
            placeholder={phaseUi.placeholder}
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendTextMessage();
              }
            }}
            disabled={composerDisabled}
          />
          {micButton}
          <button
            type="button"
            className="coach-icon-btn coach-icon-btn-send"
            onClick={sendTextMessage}
            title="Send"
            aria-label="Send"
            disabled={sendDisabled}
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a1 1 0 0 0-1.4 1.2L4.6 11.5 14 12l-9.4.5-2.6 6.7a1 1 0 0 0 1.4 1.2z"
              />
            </svg>
          </button>
        </div>
        <button
          type="button"
          className="coach-end-session-link"
          onClick={endDebrief}
          disabled={!!actionLoading || sessionEnding}
        >
          End session
        </button>
      </div>
    </div>
  );

  if (status === "active" && typeof children === "function") {
    return (
      <>
        {children({ stage: sessionStage, transcript: transcriptPanel })}
        <Dialog
          open={endWithoutSummaryOpen}
          onClose={cancelEndWithoutSummary}
          fullWidth
          maxWidth="sm"
          aria-labelledby="end-without-summary-title"
        >
          <DialogTitle id="end-without-summary-title">
            Are you sure you want to end the session without completing the summary?
          </DialogTitle>
          <DialogContent>
            <p>
              {pendingSkipReason ||
                "This conversation doesn’t have enough reflection for a debrief summary. If you end now, no summary report will be saved."}
            </p>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelEndWithoutSummary} color="primary">
              Continue coaching
            </Button>
            <Button onClick={confirmEndWithoutSummary} color="secondary">
              End without summary
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <div className="coaching-debrief-section report_item">
      {actionLoading && typeof onPageLoadingChange !== "function" && (
        <div className="overlay">
          <Loader message={actionLoading} />
        </div>
      )}

      <h2>AI Coaching Debrief</h2>
      <p>
        Reflect on your simulation with an AI coach. Speak your answers using the
        microphone, or type your response below.
      </p>

      {error && <div className="coaching-error">{error}</div>}

      {status === "idle" && (
        <div className="coaching-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => startSession()}
            disabled={!!actionLoading}
          >
            {actionLoading ? actionLoading : "Start Coaching Debrief"}
          </button>
        </div>
      )}

      {status === "active" && (
        <>
          {sessionStage}
          {transcriptPanel}
        </>
      )}

      {status === "completed" && (
        <DebriefSummarySection summary={summary} skipReason={skipReason} />
      )}

      <Dialog
        open={endWithoutSummaryOpen}
        onClose={cancelEndWithoutSummary}
        fullWidth
        maxWidth="sm"
        aria-labelledby="end-without-summary-title-fallback"
      >
        <DialogTitle id="end-without-summary-title-fallback">
          Are you sure you want to end the session without completing the summary?
        </DialogTitle>
        <DialogContent>
          <p>
            {pendingSkipReason ||
              "This conversation doesn’t have enough reflection for a debrief summary. If you end now, no summary report will be saved."}
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelEndWithoutSummary} color="primary">
            Continue coaching
          </Button>
          <Button onClick={confirmEndWithoutSummary} color="secondary">
            End without summary
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CoachingDebrief;
